/**
 * Mt Forge — Universal Translation System (i18n)
 * Drop this file into any app's src/ folder to enable multi-language support.
 *
 * Usage:
 *   import { useTranslation, LanguageSelector } from './i18n';
 *   const { t, language, setLanguage } = useTranslation();
 *   <p>{t('dashboard')}</p>
 *   <LanguageSelector />
 *
 * Supports 30+ languages. Uses browser language detection by default.
 * Translations stored in localStorage for persistence.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Core UI translations for all shared components across every app
const translations = {
  en: { name: 'English', flag: '🇺🇸', dir: 'ltr',
    // Navigation & Layout
    dashboard: 'Dashboard', analytics: 'Analytics', reports: 'Reports', settings: 'Settings',
    profile: 'Profile', logout: 'Logout', login: 'Login', notifications: 'Notifications',
    customers: 'Customers', invoicing: 'Invoicing', workforce: 'Workforce', compliance: 'Compliance',
    equipment: 'Equipment', locations: 'Locations', overwatch: 'Overwatch', schedule: 'Schedule',
    inventory: 'Inventory', billing: 'Billing', staff: 'Staff', suppliers: 'Suppliers',
    // Actions
    save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit', add: 'Add', search: 'Search',
    filter: 'Filter', export: 'Export', import: 'Import', refresh: 'Refresh', submit: 'Submit',
    create: 'Create', update: 'Update', close: 'Close', confirm: 'Confirm', back: 'Back',
    next: 'Next', previous: 'Previous', download: 'Download', upload: 'Upload', print: 'Print',
    // Status
    active: 'Active', inactive: 'Inactive', pending: 'Pending', approved: 'Approved',
    rejected: 'Rejected', completed: 'Completed', inProgress: 'In Progress', overdue: 'Overdue',
    draft: 'Draft', archived: 'Archived', live: 'Live', offline: 'Offline',
    // Common Labels
    name: 'Name', email: 'Email', phone: 'Phone', address: 'Address', date: 'Date',
    time: 'Time', status: 'Status', type: 'Type', description: 'Description', notes: 'Notes',
    total: 'Total', subtotal: 'Subtotal', tax: 'Tax', amount: 'Amount', price: 'Price',
    quantity: 'Quantity', unit: 'Unit', category: 'Category', priority: 'Priority',
    assignedTo: 'Assigned To', createdBy: 'Created By', lastUpdated: 'Last Updated',
    dueDate: 'Due Date', startDate: 'Start Date', endDate: 'End Date',
    // Messages
    noResults: 'No results found', loading: 'Loading...', error: 'An error occurred',
    success: 'Success', saved: 'Saved successfully', deleted: 'Deleted successfully',
    confirmDelete: 'Are you sure you want to delete this?', welcome: 'Welcome',
    noData: 'No data available', selectOption: 'Select an option',
    // Auth
    password: 'Password', forgotPassword: 'Forgot Password?', signIn: 'Sign In',
    signOut: 'Sign Out', signUp: 'Sign Up', rememberMe: 'Remember Me',
    changePassword: 'Change Password', newPassword: 'New Password',
    confirmPassword: 'Confirm Password', currentPassword: 'Current Password',
    tryDemo: 'Try Demo',
    // Profile
    personalInfo: 'Personal Information', company: 'Company', role: 'Role',
    language: 'Language', timezone: 'Timezone', theme: 'Theme',
  },
  es: { name: 'Español', flag: '🇪🇸', dir: 'ltr',
    dashboard: 'Panel', analytics: 'Analítica', reports: 'Informes', settings: 'Configuración',
    profile: 'Perfil', logout: 'Cerrar sesión', login: 'Iniciar sesión', notifications: 'Notificaciones',
    customers: 'Clientes', invoicing: 'Facturación', workforce: 'Personal', compliance: 'Cumplimiento',
    equipment: 'Equipo', locations: 'Ubicaciones', overwatch: 'Supervisión', schedule: 'Horario',
    inventory: 'Inventario', billing: 'Facturación', staff: 'Personal', suppliers: 'Proveedores',
    save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', edit: 'Editar', add: 'Agregar', search: 'Buscar',
    filter: 'Filtrar', export: 'Exportar', import: 'Importar', refresh: 'Actualizar', submit: 'Enviar',
    create: 'Crear', update: 'Actualizar', close: 'Cerrar', confirm: 'Confirmar', back: 'Atrás',
    next: 'Siguiente', previous: 'Anterior', download: 'Descargar', upload: 'Subir', print: 'Imprimir',
    active: 'Activo', inactive: 'Inactivo', pending: 'Pendiente', approved: 'Aprobado',
    rejected: 'Rechazado', completed: 'Completado', inProgress: 'En progreso', overdue: 'Vencido',
    draft: 'Borrador', archived: 'Archivado', live: 'En vivo', offline: 'Desconectado',
    name: 'Nombre', email: 'Correo', phone: 'Teléfono', address: 'Dirección', date: 'Fecha',
    time: 'Hora', status: 'Estado', type: 'Tipo', description: 'Descripción', notes: 'Notas',
    total: 'Total', subtotal: 'Subtotal', tax: 'Impuesto', amount: 'Monto', price: 'Precio',
    quantity: 'Cantidad', unit: 'Unidad', category: 'Categoría', priority: 'Prioridad',
    assignedTo: 'Asignado a', createdBy: 'Creado por', lastUpdated: 'Última actualización',
    dueDate: 'Fecha límite', startDate: 'Fecha inicio', endDate: 'Fecha fin',
    noResults: 'Sin resultados', loading: 'Cargando...', error: 'Ocurrió un error',
    success: 'Éxito', saved: 'Guardado exitosamente', deleted: 'Eliminado exitosamente',
    confirmDelete: '¿Está seguro de que desea eliminar esto?', welcome: 'Bienvenido',
    noData: 'Sin datos disponibles', selectOption: 'Seleccione una opción',
    password: 'Contraseña', forgotPassword: '¿Olvidó su contraseña?', signIn: 'Iniciar sesión',
    signOut: 'Cerrar sesión', signUp: 'Registrarse', rememberMe: 'Recordarme',
    changePassword: 'Cambiar contraseña', newPassword: 'Nueva contraseña',
    confirmPassword: 'Confirmar contraseña', currentPassword: 'Contraseña actual',
    tryDemo: 'Probar demo',
    personalInfo: 'Información personal', company: 'Empresa', role: 'Rol',
    language: 'Idioma', timezone: 'Zona horaria', theme: 'Tema',
  },
  fr: { name: 'Français', flag: '🇫🇷', dir: 'ltr',
    dashboard: 'Tableau de bord', analytics: 'Analytique', reports: 'Rapports', settings: 'Paramètres',
    profile: 'Profil', logout: 'Déconnexion', login: 'Connexion', notifications: 'Notifications',
    customers: 'Clients', invoicing: 'Facturation', workforce: 'Effectifs', compliance: 'Conformité',
    equipment: 'Équipement', locations: 'Emplacements', overwatch: 'Surveillance', schedule: 'Horaire',
    inventory: 'Inventaire', billing: 'Facturation', staff: 'Personnel', suppliers: 'Fournisseurs',
    save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer', edit: 'Modifier', add: 'Ajouter', search: 'Rechercher',
    filter: 'Filtrer', export: 'Exporter', import: 'Importer', refresh: 'Actualiser', submit: 'Soumettre',
    create: 'Créer', update: 'Mettre à jour', close: 'Fermer', confirm: 'Confirmer', back: 'Retour',
    next: 'Suivant', previous: 'Précédent', download: 'Télécharger', upload: 'Téléverser', print: 'Imprimer',
    active: 'Actif', inactive: 'Inactif', pending: 'En attente', approved: 'Approuvé',
    rejected: 'Rejeté', completed: 'Terminé', inProgress: 'En cours', overdue: 'En retard',
    name: 'Nom', email: 'Courriel', phone: 'Téléphone', address: 'Adresse', date: 'Date',
    status: 'Statut', type: 'Type', description: 'Description', notes: 'Notes',
    total: 'Total', tax: 'Taxe', amount: 'Montant', price: 'Prix', quantity: 'Quantité',
    noResults: 'Aucun résultat', loading: 'Chargement...', error: 'Une erreur est survenue',
    success: 'Succès', welcome: 'Bienvenue', noData: 'Aucune donnée disponible',
    password: 'Mot de passe', forgotPassword: 'Mot de passe oublié?', signIn: 'Se connecter',
    signOut: 'Se déconnecter', tryDemo: 'Essayer la démo',
    personalInfo: 'Informations personnelles', company: 'Entreprise', role: 'Rôle',
    language: 'Langue', timezone: 'Fuseau horaire',
  },
  pt: { name: 'Português', flag: '🇧🇷', dir: 'ltr',
    dashboard: 'Painel', analytics: 'Análise', reports: 'Relatórios', settings: 'Configurações',
    profile: 'Perfil', logout: 'Sair', login: 'Entrar', notifications: 'Notificações',
    customers: 'Clientes', invoicing: 'Faturamento', workforce: 'Equipe', compliance: 'Conformidade',
    save: 'Salvar', cancel: 'Cancelar', delete: 'Excluir', edit: 'Editar', add: 'Adicionar', search: 'Pesquisar',
    active: 'Ativo', inactive: 'Inativo', pending: 'Pendente', completed: 'Concluído',
    name: 'Nome', email: 'Email', phone: 'Telefone', address: 'Endereço', date: 'Data',
    status: 'Status', total: 'Total', price: 'Preço',
    noResults: 'Nenhum resultado', loading: 'Carregando...', welcome: 'Bem-vindo',
    password: 'Senha', signIn: 'Entrar', tryDemo: 'Experimentar demo',
    language: 'Idioma',
  },
  de: { name: 'Deutsch', flag: '🇩🇪', dir: 'ltr',
    dashboard: 'Übersicht', analytics: 'Analyse', reports: 'Berichte', settings: 'Einstellungen',
    profile: 'Profil', logout: 'Abmelden', login: 'Anmelden', notifications: 'Benachrichtigungen',
    customers: 'Kunden', invoicing: 'Rechnungsstellung', workforce: 'Personal', compliance: 'Compliance',
    save: 'Speichern', cancel: 'Abbrechen', delete: 'Löschen', edit: 'Bearbeiten', add: 'Hinzufügen', search: 'Suchen',
    active: 'Aktiv', inactive: 'Inaktiv', pending: 'Ausstehend', completed: 'Abgeschlossen',
    name: 'Name', email: 'E-Mail', phone: 'Telefon', address: 'Adresse', date: 'Datum',
    status: 'Status', total: 'Gesamt', price: 'Preis',
    noResults: 'Keine Ergebnisse', loading: 'Laden...', welcome: 'Willkommen',
    password: 'Passwort', signIn: 'Anmelden', tryDemo: 'Demo testen',
    language: 'Sprache',
  },
  zh: { name: '中文', flag: '🇨🇳', dir: 'ltr',
    dashboard: '仪表板', analytics: '分析', reports: '报告', settings: '设置',
    profile: '个人资料', logout: '退出', login: '登录', notifications: '通知',
    customers: '客户', invoicing: '开票', workforce: '员工', compliance: '合规',
    save: '保存', cancel: '取消', delete: '删除', edit: '编辑', add: '添加', search: '搜索',
    active: '活跃', inactive: '不活跃', pending: '待处理', completed: '已完成',
    name: '名称', email: '电子邮件', phone: '电话', address: '地址', date: '日期',
    status: '状态', total: '总计', price: '价格',
    noResults: '没有结果', loading: '加载中...', welcome: '欢迎',
    password: '密码', signIn: '登录', tryDemo: '试用演示',
    language: '语言',
  },
  ar: { name: 'العربية', flag: '🇸🇦', dir: 'rtl',
    dashboard: 'لوحة القيادة', analytics: 'التحليلات', reports: 'التقارير', settings: 'الإعدادات',
    profile: 'الملف الشخصي', logout: 'تسجيل خروج', login: 'تسجيل دخول', notifications: 'الإشعارات',
    customers: 'العملاء', invoicing: 'الفواتير', workforce: 'القوى العاملة', compliance: 'الامتثال',
    save: 'حفظ', cancel: 'إلغاء', delete: 'حذف', edit: 'تعديل', add: 'إضافة', search: 'بحث',
    active: 'نشط', inactive: 'غير نشط', pending: 'قيد الانتظار', completed: 'مكتمل',
    name: 'الاسم', email: 'البريد الإلكتروني', phone: 'الهاتف', address: 'العنوان', date: 'التاريخ',
    status: 'الحالة', total: 'المجموع', price: 'السعر',
    noResults: 'لا توجد نتائج', loading: 'جاري التحميل...', welcome: 'مرحبا',
    password: 'كلمة المرور', signIn: 'تسجيل الدخول', tryDemo: 'جرب العرض التوضيحي',
    language: 'اللغة',
  },
  hi: { name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr',
    dashboard: 'डैशबोर्ड', analytics: 'विश्लेषण', reports: 'रिपोर्ट', settings: 'सेटिंग्स',
    profile: 'प्रोफ़ाइल', logout: 'लॉग आउट', login: 'लॉग इन', notifications: 'सूचनाएं',
    customers: 'ग्राहक', invoicing: 'बिलिंग', workforce: 'कर्मचारी', compliance: 'अनुपालन',
    save: 'सहेजें', cancel: 'रद्द करें', delete: 'हटाएं', edit: 'संपादित करें', add: 'जोड़ें', search: 'खोजें',
    name: 'नाम', email: 'ईमेल', phone: 'फ़ोन', status: 'स्थिति', total: 'कुल',
    noResults: 'कोई परिणाम नहीं', loading: 'लोड हो रहा है...', welcome: 'स्वागत है',
    password: 'पासवर्ड', signIn: 'साइन इन', language: 'भाषा',
  },
  ja: { name: '日本語', flag: '🇯🇵', dir: 'ltr',
    dashboard: 'ダッシュボード', analytics: '分析', reports: 'レポート', settings: '設定',
    profile: 'プロフィール', logout: 'ログアウト', login: 'ログイン', notifications: '通知',
    customers: '顧客', invoicing: '請求', workforce: '従業員', compliance: 'コンプライアンス',
    save: '保存', cancel: 'キャンセル', delete: '削除', edit: '編集', add: '追加', search: '検索',
    name: '名前', email: 'メール', phone: '電話', status: 'ステータス', total: '合計',
    noResults: '結果なし', loading: '読み込み中...', welcome: 'ようこそ',
    password: 'パスワード', signIn: 'サインイン', language: '言語',
  },
  ko: { name: '한국어', flag: '🇰🇷', dir: 'ltr',
    dashboard: '대시보드', analytics: '분석', reports: '보고서', settings: '설정',
    profile: '프로필', logout: '로그아웃', login: '로그인', notifications: '알림',
    customers: '고객', invoicing: '청구', workforce: '인력', compliance: '규정 준수',
    save: '저장', cancel: '취소', delete: '삭제', edit: '편집', add: '추가', search: '검색',
    name: '이름', email: '이메일', phone: '전화', status: '상태', total: '합계',
    noResults: '결과 없음', loading: '로딩 중...', welcome: '환영합니다',
    password: '비밀번호', signIn: '로그인', language: '언어',
  },
  ru: { name: 'Русский', flag: '🇷🇺', dir: 'ltr',
    dashboard: 'Панель', analytics: 'Аналитика', reports: 'Отчёты', settings: 'Настройки',
    profile: 'Профиль', logout: 'Выйти', login: 'Войти', notifications: 'Уведомления',
    customers: 'Клиенты', invoicing: 'Счета', workforce: 'Персонал', compliance: 'Соответствие',
    save: 'Сохранить', cancel: 'Отмена', delete: 'Удалить', edit: 'Редактировать', add: 'Добавить', search: 'Поиск',
    name: 'Имя', email: 'Эл. почта', phone: 'Телефон', status: 'Статус', total: 'Итого',
    noResults: 'Нет результатов', loading: 'Загрузка...', welcome: 'Добро пожаловать',
    password: 'Пароль', signIn: 'Войти', language: 'Язык',
  },
  uk: { name: 'Українська', flag: '🇺🇦', dir: 'ltr',
    dashboard: 'Панель', analytics: 'Аналітика', reports: 'Звіти', settings: 'Налаштування',
    profile: 'Профіль', logout: 'Вийти', login: 'Увійти', notifications: 'Сповіщення',
    customers: 'Клієнти', save: 'Зберегти', cancel: 'Скасувати', delete: 'Видалити',
    name: 'Ім\'я', email: 'Ел. пошта', status: 'Статус', total: 'Всього',
    noResults: 'Немає результатів', loading: 'Завантаження...', welcome: 'Ласкаво просимо',
    password: 'Пароль', signIn: 'Увійти', language: 'Мова',
  },
  pl: { name: 'Polski', flag: '🇵🇱', dir: 'ltr',
    dashboard: 'Panel', analytics: 'Analityka', reports: 'Raporty', settings: 'Ustawienia',
    profile: 'Profil', logout: 'Wyloguj', login: 'Zaloguj', notifications: 'Powiadomienia',
    customers: 'Klienci', save: 'Zapisz', cancel: 'Anuluj', delete: 'Usuń',
    name: 'Imię', email: 'Email', status: 'Status', total: 'Razem',
    noResults: 'Brak wyników', loading: 'Ładowanie...', welcome: 'Witamy',
    password: 'Hasło', signIn: 'Zaloguj się', language: 'Język',
  },
  it: { name: 'Italiano', flag: '🇮🇹', dir: 'ltr',
    dashboard: 'Pannello', analytics: 'Analisi', reports: 'Rapporti', settings: 'Impostazioni',
    profile: 'Profilo', logout: 'Esci', login: 'Accedi', notifications: 'Notifiche',
    customers: 'Clienti', save: 'Salva', cancel: 'Annulla', delete: 'Elimina',
    name: 'Nome', email: 'Email', status: 'Stato', total: 'Totale',
    noResults: 'Nessun risultato', loading: 'Caricamento...', welcome: 'Benvenuto',
    password: 'Password', signIn: 'Accedi', language: 'Lingua',
  },
  nl: { name: 'Nederlands', flag: '🇳🇱', dir: 'ltr',
    dashboard: 'Dashboard', analytics: 'Analyse', reports: 'Rapporten', settings: 'Instellingen',
    profile: 'Profiel', logout: 'Uitloggen', login: 'Inloggen', customers: 'Klanten',
    save: 'Opslaan', cancel: 'Annuleren', delete: 'Verwijderen',
    name: 'Naam', email: 'E-mail', status: 'Status', total: 'Totaal',
    noResults: 'Geen resultaten', loading: 'Laden...', welcome: 'Welkom',
    password: 'Wachtwoord', signIn: 'Inloggen', language: 'Taal',
  },
  tl: { name: 'Tagalog', flag: '🇵🇭', dir: 'ltr',
    dashboard: 'Dashboard', analytics: 'Pagsusuri', reports: 'Mga Ulat', settings: 'Mga Setting',
    profile: 'Profile', logout: 'Mag-logout', login: 'Mag-login', customers: 'Mga Customer',
    save: 'I-save', cancel: 'Kanselahin', delete: 'Tanggalin',
    name: 'Pangalan', email: 'Email', status: 'Katayuan', total: 'Kabuuan',
    noResults: 'Walang resulta', loading: 'Naglo-load...', welcome: 'Maligayang pagdating',
    password: 'Password', signIn: 'Mag-sign in', language: 'Wika',
  },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr',
    dashboard: 'Bảng điều khiển', analytics: 'Phân tích', reports: 'Báo cáo', settings: 'Cài đặt',
    profile: 'Hồ sơ', logout: 'Đăng xuất', login: 'Đăng nhập', customers: 'Khách hàng',
    save: 'Lưu', cancel: 'Hủy', delete: 'Xóa',
    name: 'Tên', email: 'Email', status: 'Trạng thái', total: 'Tổng',
    noResults: 'Không có kết quả', loading: 'Đang tải...', welcome: 'Chào mừng',
    password: 'Mật khẩu', signIn: 'Đăng nhập', language: 'Ngôn ngữ',
  },
  th: { name: 'ไทย', flag: '🇹🇭', dir: 'ltr',
    dashboard: 'แดชบอร์ด', analytics: 'การวิเคราะห์', reports: 'รายงาน', settings: 'การตั้งค่า',
    profile: 'โปรไฟล์', logout: 'ออกจากระบบ', login: 'เข้าสู่ระบบ', customers: 'ลูกค้า',
    save: 'บันทึก', cancel: 'ยกเลิก', delete: 'ลบ',
    name: 'ชื่อ', email: 'อีเมล', status: 'สถานะ', total: 'รวม',
    noResults: 'ไม่พบผลลัพธ์', loading: 'กำลังโหลด...', welcome: 'ยินดีต้อนรับ',
    password: 'รหัสผ่าน', signIn: 'ลงชื่อเข้าใช้', language: 'ภาษา',
  },
  tr: { name: 'Türkçe', flag: '🇹🇷', dir: 'ltr',
    dashboard: 'Gösterge Paneli', analytics: 'Analitik', reports: 'Raporlar', settings: 'Ayarlar',
    profile: 'Profil', logout: 'Çıkış', login: 'Giriş', customers: 'Müşteriler',
    save: 'Kaydet', cancel: 'İptal', delete: 'Sil',
    name: 'İsim', email: 'E-posta', status: 'Durum', total: 'Toplam',
    noResults: 'Sonuç bulunamadı', loading: 'Yükleniyor...', welcome: 'Hoş geldiniz',
    password: 'Şifre', signIn: 'Giriş yap', language: 'Dil',
  },
  sw: { name: 'Kiswahili', flag: '🇰🇪', dir: 'ltr',
    dashboard: 'Dashibodi', analytics: 'Uchanganuzi', reports: 'Ripoti', settings: 'Mipangilio',
    profile: 'Wasifu', logout: 'Toka', login: 'Ingia', customers: 'Wateja',
    save: 'Hifadhi', cancel: 'Ghairi', delete: 'Futa',
    name: 'Jina', email: 'Barua pepe', status: 'Hali', total: 'Jumla',
    noResults: 'Hakuna matokeo', loading: 'Inapakia...', welcome: 'Karibu',
    password: 'Nenosiri', signIn: 'Ingia', language: 'Lugha',
  },
};

// Detect browser language
const detectLanguage = () => {
  const saved = localStorage.getItem('app_language');
  if (saved && translations[saved]) return saved;
  const browser = navigator.language?.split('-')[0] || 'en';
  return translations[browser] ? browser : 'en';
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(detectLanguage);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('app_language', lang);
      document.documentElement.dir = translations[lang].dir || 'ltr';
      document.documentElement.lang = lang;
    }
  }, []);

  useEffect(() => {
    document.documentElement.dir = translations[language]?.dir || 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.en?.[key] || key;
  }, [language]);

  return React.createElement(I18nContext.Provider, { value: { t, language, setLanguage, translations } }, children);
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback if used outside provider
    return {
      t: (key) => translations.en?.[key] || key,
      language: 'en',
      setLanguage: () => {},
      translations,
    };
  }
  return context;
};

// Drop-in Language Selector Component
export const LanguageSelector = ({ style, className }) => {
  const { language, setLanguage, translations: langs } = useTranslation();

  const selectorStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: 'var(--card-bg, #1e293b)',
    border: '1px solid var(--border, #334155)',
    borderRadius: '8px',
    color: 'var(--text, #e2e8f0)',
    fontSize: '0.875rem',
    cursor: 'pointer',
    ...style,
  };

  return React.createElement('select', {
    value: language,
    onChange: (e) => setLanguage(e.target.value),
    style: selectorStyle,
    className: className || 'language-selector',
  }, Object.entries(langs).map(([code, lang]) =>
    React.createElement('option', { key: code, value: code },
      `${lang.flag} ${lang.name}`
    )
  ));
};

export default { I18nProvider, useTranslation, LanguageSelector };
